import { QueryAggregator } from "@/api/utils/QueryAggregator"
import { AccountService } from "@/services/AccountService"
import { DebtPaymentPlanService } from "@/services/DebtPaymentPlanService"
import { DirectDebitService } from "@/services/DirectDebitService"
import { getDirectDebitDefaultMandate } from "@/helpers/directDebitHelper"
import { PaymentScheduleService } from "@/services/PaymentScheduleService"
import { PAYMENT_PLAN } from "@/constants/paymentPlan"

export const getDebtPaymentPlanEligibility = (accountNumber: string) => {
  const queries = new QueryAggregator()

  const accountQuery = AccountService.getAccount(accountNumber)
  const coreIdQuery = AccountService.getCoreIdByAccountNumber(accountNumber)
  const directDebitQuery = DirectDebitService.getForAccount(coreIdQuery.data)
  const planOptionsQuery =
    DebtPaymentPlanService.getPlanOptionsForAccount(accountNumber)
  const activeDebtPaymentPlanQuery =
    DebtPaymentPlanService.activeDebtPaymentPlan(accountNumber)
  const hasActivePaymentScheduleQuery =
    PaymentScheduleService.hasActivePaymentSchedule(accountNumber)

  queries.add(accountQuery)
  queries.add(coreIdQuery)
  queries.add(planOptionsQuery)
  queries.add(directDebitQuery)
  queries.add(activeDebtPaymentPlanQuery)
  queries.add(hasActivePaymentScheduleQuery)

  const account = accountQuery.data
  const ddMandate = getDirectDebitDefaultMandate(directDebitQuery.data)
  const hasDDMandate = Boolean(ddMandate?.isActive || ddMandate?.isPending)
  const hasPlanOptions = Boolean(planOptionsQuery.data)
  const isFinalBillPlanOptions =
    planOptionsQuery.data?.planType === PAYMENT_PLAN
  const hasActiveDebtPaymentPlan = !!activeDebtPaymentPlanQuery.data
  const hasActivePaymentSchedule = !!hasActivePaymentScheduleQuery.data

  const commonChecks =
    hasPlanOptions &&
    hasDDMandate &&
    account.balance > 0 &&
    !hasActiveDebtPaymentPlan

  const isEligible = account.isTerminating
    ? commonChecks && isFinalBillPlanOptions
    : account.isActive && commonChecks && hasActivePaymentSchedule

  let ineligibilityReason = ""
  if (!isEligible) {
    if (!(account.isActive || account.isTerminating)) {
      ineligibilityReason = "Account not active"
    } else if (account.balance < 0) {
      ineligibilityReason = "Account not in debt"
    } else if (
      !hasPlanOptions ||
      (account.isTerminating && !isFinalBillPlanOptions)
    ) {
      ineligibilityReason = "No payment plan available for this account"
    } else if (!hasDDMandate) {
      ineligibilityReason = "No active direct debit on account"
    } else if (!account.isTerminating && !hasActivePaymentSchedule) {
      ineligibilityReason = "Account has no active payment schedule"
    }
  }

  const eligibilityBreakdown = {
    isEligible: isEligible,
    ineligibilityReason: ineligibilityReason,
  }

  return queries.setData(eligibilityBreakdown).getAggregate()
}
